/**
 * Incoming-call cards — the macOS "call from your iPhone" look, top-right.
 *
 * One card per call Xale just routed (dynamic call routing): avatar, caller,
 * the lead, where it is ringing. The routed counsellor sees their own calls;
 * tenant admins see every call in the workspace (the server decides who
 * receives the push). "Open lead" opens the lead drawer so the counsellor
 * has it in front of them when they pick up the phone; "Decline" only hides
 * the card — the phone keeps ringing (answering in the browser needs the
 * telephony provider's web calling, which is not wired yet).
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Phone, PhoneOff, X } from "lucide-react";
import axiosInstance from "@/lib/axios";
import { leadSourceEndpoint } from "@/config/endpoints";
import { cn } from "@/lib/utils";
import { currentUserId } from "@/lib/currentUserId";
import { getInitials } from "@/utils/convetors";
import { useSocketListener } from "@/sockets/hooks/useSocketListener";
import { SocketEvents } from "@/sockets/socketEvents";
import { useOpenLead } from "@/features/Dashboard/v2/activity/useOpenLead";
import { dismissCall, endCall, ringCall, seedCalls, useLiveCalls, type LiveCall, type LiveCallEnd } from "./liveCalls";

const MAX_CARDS = 4;

function since(iso: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

function CallCard({ call, mine }: { call: LiveCall; mine: boolean }) {
  const openLead = useOpenLead();
  const title = call.leadName || call.from || "Unknown caller";
  const subtitle = call.leadName && call.from ? call.from : call.sourceName ? `via ${call.sourceName}` : "";
  const ringingTo = call.userName
    ? mine
      ? `Ringing on your phone${call.destination ? ` · ${call.destination}` : ""}`
      : `Ringing ${call.userName}${call.destination ? ` · ${call.destination}` : ""}`
    : call.destination
      ? `Ringing ${call.reason === "did" ? "the line's number" : "the default number"} · ${call.destination}`
      : call.leadId
        ? "Nobody assigned in time — the provider plays its own fallback"
        : "Caller not routed (no lead, or the lead is in Trash) — provider fallback";

  return (
    <div
      role="status"
      className={cn(
        "pointer-events-auto w-[340px] rounded-2xl border border-black/5 bg-white/95 shadow-[0_12px_40px_rgba(5,25,18,0.18)] backdrop-blur",
        "animate-in slide-in-from-right-6 fade-in duration-300",
      )}
    >
      <div className="flex items-start gap-3 p-3">
        <div className="relative shrink-0">
          <div className="flex size-12 items-center justify-center rounded-full bg-[#edfaf5] text-[15px] font-semibold text-[#156548]">
            {getInitials(title) || <Phone className="size-5" />}
          </div>
          <span className="absolute -inset-1 rounded-full border-2 border-[#6fb99c]/60 animate-ping" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-[#697571]">
            {mine ? "Incoming call" : "Incoming call · live"}
            <span className="ml-2 text-[#9ba3a0]">{since(call.at)}</span>
          </p>
          <p className="truncate text-[15px] font-semibold text-[#051912]">{title}</p>
          {subtitle && <p className="truncate text-[12px] text-[#505e59]">{subtitle}</p>}
          <p className="mt-1 truncate text-[12px] text-[#156548]">
            {ringingTo}
            {call.leadCreated ? " · new lead" : ""}
          </p>
        </div>
        <button
          type="button"
          aria-label="Hide"
          onClick={() => dismissCall(call.callKey)}
          className="size-7 shrink-0 rounded-full text-[#697571] hover:bg-[#f0f3f2] flex items-center justify-center"
        >
          <X className="size-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 px-3 pb-3">
        <button
          type="button"
          onClick={() => dismissCall(call.callKey)}
          className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-[#fdecef] text-[13px] font-medium text-[#da2a46] hover:bg-[#fbdde2]"
        >
          <PhoneOff className="size-4" /> Decline
        </button>
        <button
          type="button"
          disabled={!call.leadId}
          onClick={() => {
            if (call.leadId) openLead(call.leadId);
            dismissCall(call.callKey);
          }}
          className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-[#156548] text-[13px] font-medium text-white hover:bg-[#124f39] disabled:opacity-50"
          title={mine ? "Opens the lead — the call connects on your phone" : "Open the lead"}
        >
          <Phone className="size-4" /> {mine ? "Accept call" : "Open lead"}
        </button>
      </div>
      {mine && (
        <p className="px-3 pb-2 -mt-1 text-[10px] text-[#9ba3a0]">
          Connects on your phone · answering with the laptop mic needs the provider's web calling
        </p>
      )}
    </div>
  );
}

export default function IncomingCallHost() {
  const calls = useLiveCalls();
  const me = currentUserId();

  // Fill in whatever is ringing right now (page load / reconnect).
  const seed = useQuery({
    queryKey: ["call-routing-ringing"],
    queryFn: async () => {
      const res = await axiosInstance.get(`${leadSourceEndpoint}/call-routing/ringing`);
      return (res.data?.calls ?? []) as LiveCall[];
    },
    staleTime: 15_000,
    retry: false,
  });
  useEffect(() => {
    if (seed.data) seedCalls(seed.data);
  }, [seed.data]);

  useSocketListener(SocketEvents.CALL_ROUTING_RINGING, (payload: LiveCall) => {
    if (payload?.callKey) ringCall(payload);
  });
  useSocketListener(SocketEvents.CALL_ROUTING_ENDED, (payload: LiveCallEnd) => {
    if (payload?.callKey) endCall(payload);
  });

  if (!calls.length) return null;
  const shown = calls.slice(0, MAX_CARDS);
  const hidden = calls.length - shown.length;

  return (
    <div className="pointer-events-none fixed right-4 top-[72px] z-[60] flex flex-col gap-2">
      {shown.map((c) => (
        <CallCard key={c.callKey} call={c} mine={me != null && c.userId === me} />
      ))}
      {hidden > 0 && <p className="pointer-events-auto text-right text-[11px] text-[#697571]">+{hidden} more ringing</p>}
    </div>
  );
}
